import styled from "styled-components";
import { breakpoints } from "../../../utils/breakpoints";

/**
 * v2 design system for the landing sections: a continuous #000104 canvas
 * matching the hero, with a faint blueprint grid, per-section accent glow,
 * numbered overline labels and glass-morphism cards.
 */

export const accents = {
	green: "#9FE870",
	lime: "#CBFA05",
	violet: "#7D72FF",
	cyan: "#0AE1FF",
};

export const SectionV2 = styled.section`
  position: relative;
  background: #000104;
  overflow: hidden;
  padding: clamp(72px, 9vw, 120px) 24px;
  /* each section owns at least one full viewport, content centered in it;
     taller content just grows past it */
  min-height: 100vh;
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  justify-content: center;

  /* accent atmosphere */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(
      ellipse 55% 42% at ${(p) => p.$glowAt || "80% 0%"},
      ${(p) => (p.$accent || accents.violet) + "14"} 0%,
      transparent 70%
    );
  }

  @media (min-width: ${breakpoints.m}) {
    padding-left: 64px;
    padding-right: 64px;
  }
`;

export const InnerV2 = styled.div`
  position: relative;
  z-index: 1;
  /* explicit width: as a flex item of SectionV2, auto margins would
     otherwise shrink it to fit-content */
  width: 100%;
  max-width: 1216px;
  margin: 0 auto;
`;

export const HeadV2 = styled.header`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$align === "left" ? "flex-start" : "center")};
  text-align: ${(p) => (p.$align === "left" ? "left" : "center")};
  margin-bottom: clamp(40px, 5vw, 72px);

  .overline {
    display: inline-flex;
    align-items: center;
    gap: 14px;
    font-family: "Hind Madurai";
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 4px;
    text-transform: uppercase;
    color: ${(p) => p.$accent || accents.violet};
    margin-bottom: 14px;
    /* Tailwind ships a global ".overline { text-decoration: overline }"
       utility that collides with this class name — neutralize it */
    text-decoration: none;

    &::before,
    &::after {
      content: "";
      height: 1px;
      width: 38px;
      background: linear-gradient(
        90deg,
        transparent,
        ${(p) => (p.$accent || accents.violet) + "aa"}
      );
    }

    &::after {
      background: linear-gradient(
        270deg,
        transparent,
        ${(p) => (p.$accent || accents.violet) + "aa"}
      );
    }
  }

  h2 {
    font-family: "Hind Madurai";
    font-weight: 600;
    font-size: clamp(2.1rem, 4.2vw, 3.6rem);
    line-height: 1.16;
    letter-spacing: -0.6px;
    color: #ffffff;
    margin: 0 0 14px 0;
    text-wrap: balance;

    em {
      font-style: normal;
      color: ${(p) => p.$accent || accents.violet};
      text-shadow: 0 0 30px ${(p) => (p.$accent || accents.violet) + "59"};
    }
  }

  p {
    font-family: "Hind Madurai";
    font-size: clamp(0.95rem, 1.25vw, 1.15rem);
    line-height: 1.7;
    color: #a9afb8;
    max-width: 720px;
    margin: 0;
  }
`;

export const GlassCard = styled.div`
  position: relative;
  background: linear-gradient(
    165deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.015) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  transition:
    transform 0.35s cubic-bezier(0.16, 1, 0.3, 1),
    border-color 0.35s ease,
    box-shadow 0.35s ease,
    background 0.35s ease;

  &:hover {
    transform: translateY(-6px);
    background: linear-gradient(
      165deg,
      rgba(255, 255, 255, 0.07) 0%,
      rgba(255, 255, 255, 0.02) 100%
    );
    border-color: ${(p) => (p.$accent || accents.violet) + "66"};
    box-shadow:
      0 24px 64px -24px ${(p) => (p.$accent || accents.violet) + "40"},
      0 0 0 1px ${(p) => (p.$accent || accents.violet) + "1a"};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

export const PillButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: "Hind Madurai";
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1.4px;
  color: ${(p) => p.$accent || accents.violet};
  background: transparent;
  border: 1.5px solid ${(p) => (p.$accent || accents.violet) + "99"};
  border-radius: 100px;
  padding: 12px 30px;
  cursor: pointer;
  /* it is often rendered as an <a> so crawlers can follow the destination */
  text-decoration: none;
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    box-shadow 0.3s ease,
    transform 0.2s ease;

  /* globals.scss applies "* { color: #210E3F }" straight on the svg AND
     its inner path (where stroke="currentColor" actually resolves) —
     force the whole subtree back to the button's color */
  svg,
  svg * {
    color: inherit;
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover {
    background-color: ${(p) => p.$accent || accents.violet};
    color: #000104;
    box-shadow: 0 0 36px ${(p) => (p.$accent || accents.violet) + "59"};
    transform: translateY(-2px);

    svg {
      transform: translateX(4px);
    }
  }
`;

/** thin gradient seam between sections, keeps the canvas continuous */
export const SeamV2 = styled.div`
  position: relative;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.1) 50%,
    transparent 100%
  );
`;
